import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// ─── Helpers ───────────────────────────────────────────────────────────────

const fmt = (value) =>
  value !== null && value !== undefined
    ? Number(value).toLocaleString('es-CL')
    : '';

// ─── Cabecera compartida ───────────────────────────────────────────────────

/**
 * Construye las líneas de cabecera con datos de empresa y período.
 * Se usa tanto en PDF como en Excel para mantener consistencia.
 */
function buildHeaderLines(empresa, periodo) {
  return [
    ['BALANCE GENERAL'],
    [`Empresa:  ${empresa.nombre}`],
    [`RUT:      ${empresa.rut}`],
    [`Dirección: ${empresa.direccion}${empresa.comuna ? ', ' + empresa.comuna : ''}${empresa.ciudad ? ', ' + empresa.ciudad : ''}`],
    [`Período:  ${periodo.label}`],
  ];
}

// ─── Columnas de la tabla ──────────────────────────────────────────────────

const COLUMNS = [
  { header: 'Cuenta',   key: 'cuenta'   },
  { header: 'Debe',     key: 'debe'     },
  { header: 'Haber',    key: 'haber'    },
  { header: 'Deudor',   key: 'deudor'   },
  { header: 'Acreedor', key: 'acreedor' },
  { header: 'Activo',   key: 'activo'   },
  { header: 'Pasivo',   key: 'pasivo'   },
  { header: 'Pérdida',  key: 'perdida'  },
  { header: 'Ganancia', key: 'ganancia' },
];

// ─── PDF ───────────────────────────────────────────────────────────────────

/**
 * Genera y descarga el Balance General en formato PDF.
 * Usa únicamente los datos ya disponibles en el frontend (sin llamadas extra al servidor).
 *
 * @param {{ empresa: Object, periodo: Object, filas: Array }} exportData
 */
export function generarPDF({ empresa, periodo, filas }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });

  const pageWidth = doc.internal.pageSize.getWidth();
  let cursorY = 40;

  // ── Título ──
  doc.setFontSize(14).setFont('helvetica', 'bold');
  doc.text('BALANCE GENERAL', pageWidth / 2, cursorY, { align: 'center' });
  cursorY += 20;

  // ── Datos empresa ──
  doc.setFontSize(10).setFont('helvetica', 'normal');
  doc.text(`Empresa:   ${empresa.nombre}`, 40, cursorY);
  cursorY += 14;
  doc.text(`RUT:       ${empresa.rut}`, 40, cursorY);
  cursorY += 14;
  const direccionCompleta = [empresa.direccion, empresa.comuna, empresa.ciudad].filter(Boolean).join(', ');
  doc.text(`Dirección: ${direccionCompleta}`, 40, cursorY);
  cursorY += 14;
  doc.text(`Período:   ${periodo.label}`, 40, cursorY);
  cursorY += 20;

  // ── Tabla ──
  autoTable(doc, {
    startY: cursorY,
    head: [COLUMNS.map((c) => c.header)],
    body: filas.map((row) =>
      COLUMNS.map((c) => (c.key === 'cuenta' ? row[c.key] : fmt(row[c.key])))
    ),
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles: {
      0: { cellWidth: 160 },
    },
  });

  // ── Firma del representante legal ──
  const finalY = doc.lastAutoTable.finalY + 40;
  doc.setFontSize(10).setFont('helvetica', 'normal');
  doc.text('_______________________________', 40, finalY);
  doc.text('Firma del Representante Legal', 40, finalY + 14);
  doc.text(`${empresa.rep_legal || ''}`, 40, finalY + 28);
  doc.text(`RUT: ${empresa.rut_representante || ''}`, 40, finalY + 42);

  doc.save(`balance-${periodo.mes.toString().padStart(2, '0')}-${periodo.anual}.pdf`);
}

// ─── Excel ─────────────────────────────────────────────────────────────────

/**
 * Genera y descarga el Balance General en formato Excel (.xlsx).
 * Usa únicamente los datos ya disponibles en el frontend.
 *
 * @param {{ empresa: Object, periodo: Object, filas: Array }} exportData
 */
export function generarExcel({ empresa, periodo, filas }) {
  const wb = XLSX.utils.book_new();

  // Cabecera
  const headerLines = buildHeaderLines(empresa, periodo).map(([v]) => [v]);
  const blankRow = [''];

  // Filas de datos
  const tableHeaders = [COLUMNS.map((c) => c.header)];
  const tableRows = filas.map((row) =>
    COLUMNS.map((c) => {
      if (c.key === 'cuenta') return row[c.key];
      const n = Number(row[c.key]);
      return isNaN(n) ? '' : n;
    })
  );

  // Firma
  const firmaRows = [
    blankRow,
    [''],
    ['_______________________________'],
    ['Firma del Representante Legal'],
    [empresa.rep_legal || ''],
    [`RUT: ${empresa.rut_representante || ''}`],
  ];

  const wsData = [
    ...headerLines,
    blankRow,
    ...tableHeaders,
    ...tableRows,
    ...firmaRows,
  ];

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Ancho de columnas
  ws['!cols'] = [{ wch: 40 }, ...Array(COLUMNS.length - 1).fill({ wch: 16 })];

  XLSX.utils.book_append_sheet(wb, ws, 'Balance General');
  XLSX.writeFile(wb, `balance-${periodo.mes.toString().padStart(2, '0')}-${periodo.anual}.xlsx`);
}
